// Libraries
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import moment from 'moment';

// MUI
import {
    FormControl,
    FormControlLabel,
    RadioGroup,
    Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';

// Core
import { ParameterValues, ResourceTextApplication, paramRepository } from '../../../core/resources';
import FormValidator from '../../../core/FormValidator';
import { dayFormat } from '../../../core/constants';
import authorization from '../../../core/authorization';

// Common
import {
    ADialog,
    ADialogActions,
    ADialogButtonsProp,
    ADialogContent,
    ADialogTitle,
} from '../../../common/dialog';
import { ADateInput, AMultiselect, ASwitch, ATextarea, ATextInput } from '../../../common/inputs';

// Interfaces
import {
    IAnimalSymptoms,
    IChainInformationData,
    IChainInformationUpdateData,
    IAnimalDiseases,
    IAnimalInputValue,
    IAnimalSymptomsToSave,
    ILoadedSymptoms,
} from '../IChainInformationDialog';
import { IAnimalAnnouncement, IAnimalAnnouncementAnimal } from '../../../interfaces/IAnnouncement';
import { GUIDType } from '../../../interfaces/types';

// Store
import contextUtils from '../../../core/utils.context';
import { useAppSelector } from '../../../store/hooks';

// API
import chainInformationApi from '../../../api/chainInformationApi';
import { ARadiobutton } from '../../../common/buttons/ARadioButton';
import { ACheckbox } from '../../../common/buttons/ACheckbox';
import ChainInfoMedication, { IMedicationInfo, IMedicationEntry } from './ChainInfoMedication';

export interface IChainInformationDialogProps {
    onClose: (cancelled?: boolean, relatedAnnouncementId?: string) => any;
    announcementData: IAnimalAnnouncement | null;
    announcementId: GUIDType;
    announcementNumber: string;
    animals: IAnimalAnnouncementAnimal[] | null;
    // pass validators from the outside to avoid reinitialization on each render
    validator: FormValidator;
    // for custom number validation with custom error message
    medicationNumberFieldValidator: FormValidator;
    companyName?: string;
    producerNumber?: string;
    forceReadOnly?: boolean;
    newAnnouncement?: boolean;
}

export interface IMedicatedAnimals {
    animalId: string;
    isMedicated: boolean;
}

export default function ChainInformationDialog(props: IChainInformationDialogProps) {
    const { validator, medicationNumberFieldValidator } = props;
    const { t } = useTranslation<ResourceTextApplication[]>(['AnelmaChainInformation', 'AnelmaGeneral']);
    const context = useAppSelector((state) => state.context);
    const user = useAppSelector((state) => state.loginStatus);
    const parsedContext = contextUtils.parseFarmContext(context.data.currentContext?.context || '');
    const [symptoms, setSymptoms] = useState<ParameterValues>([]);
    const [diseases, setDiseases] = useState<ParameterValues>([]);

    const auth = authorization;

    const { enqueueSnackbar } = useSnackbar();

    useEffect(() => {
        setIsLoading(true);
        paramRepository
            .load([
                'AnelmaGeneral',
                'AnelmaChainInformation',
                'AnelmaLayout',
                'AnelmaAnimalPayload',
            ])
            .then(() => {
                setSymptoms(paramRepository.resource('AnelmaChainInformation', 'BovineSymptoms'));
                setDiseases(paramRepository.resource('AnelmaChainInformation', 'BovineDisease'));
                const salesTypes = paramRepository.resource('AnelmaAnimalPayload', 'SalesType');

                if (salesTypes) setSalesTypeParams(salesTypes);
                setIsLoading(false);
            });
    }, []);

    const [isLoading, setIsLoading] = useState<boolean>();
    const [validationState, setValidationState] = useState<any>({
        healthInformation: true,
        gmFree: true,
    });
    const [medicatedFeed, setMedicatedFeed] = useState<boolean | null>(null);
    const [healthInformation, setHealthInformation] = useState<boolean | null>(null);
    const [authorityRegulations, setAuthorityRegulations] = useState<boolean | null>(null);
    const [foreignObject, setForeignObject] = useState<boolean | null>(null);
    const [gmFree, setGmFree] = useState<boolean | null>(null);
    const [medicalAccounting, setMedicalAccounting] = useState<boolean | null>(false);
    const [waitingTimeOver, setWaitingTimeOver] = useState<boolean | null>(false);
    const [nasevaInfo, setNasevaInfo] = useState<boolean>(false);
    const [slaughterHouseInfo, setSlaughterHouseInfo] = useState<boolean>(false);
    const [medicationInfo, setMedicationInfo] = useState<string>('');
    const [anomalies, setAnomalies] = useState<string>('');
    const [regulations, setRegulations] = useState<string>('');
    const [samplingOrder, setSamplingOrder] = useState<string>('');
    const [vetName, setVetName] = useState<string>('');
    const [additionalInfo, setAdditionalInfo] = useState<string>('');
    const [foreignObjectInfo, setForeignObjectInfo] = useState<string>('');
    const [signature, setSignature] = useState<{ signatory: string; signatureDate: moment.Moment }>(
        {
            signatory: `${user.data.firstName}  ${user.data.lastName}`,
            signatureDate: moment(),
        },
    );
    const [animalSymptoms, setAnimalSymptoms] = useState<IAnimalSymptoms[]>([]);
    const [otherSymptom, setOtherSymptom] = useState<string>('');
    const [animalSymptomCodes, setAnimalSymptomCodes] = useState<string[]>([]);
    const [animalDiseaseCodes, setAnimalDiseaseCodes] = useState<string[]>([]);
    const [animalDiseases, setAnimalDiseases] = useState<IAnimalDiseases[]>([]);
    const [otherDisease, setOtherDisease] = useState<string>('');
    const [medicatedAnimals, setMedicatedAnimals] = useState<IMedicatedAnimals[]>([]);
    const [medicationInfoList, setMedicationInfoList] = useState<IMedicationInfo[]>([]);
    const [confirmation, setConfirmation] = useState<boolean>(false);
    const [formConfirmation, setFormConfirmation] = useState<boolean>(false);
    const [mode, setMode] = useState<string>('add');
    const [chainInformationId, setChainInformationId] = useState<GUIDType>('');
    const [salesTypeParams, setSalesTypeParams] = useState<ParameterValues | null>(null);
    const [announcementNumber, setAnnouncementNumber] = useState<string>('');

    const [loadedSymptoms, setLoadedSymptoms] = useState<ILoadedSymptoms[]>([]);

    const [readOnly, setReadOnly] = useState<boolean>(false);

    const [animals, setAnimals] = useState<IAnimalAnnouncementAnimal[]>([]);
    const [announcementId] = useState<GUIDType>(
        props.announcementId || props.announcementData?.Id || '',
    );
    const [animalsInput, setAnimalsInput] = useState<IAnimalInputValue[]>([]);

    const filterValidator = () => {
        // TODO: remove this and do it in a correct way
        for (let animal of animals) {
            let ai = medicationInfoList.findIndex((info) => info.animalId === animal.AnimalId);
            if (ai != -1) {
                medicationInfoList[ai].medications.forEach((m, i) => {
                    validator.removeState(`medication-name-${animal.AnimalId}-${i}`);
                    validator.removeState(`medication-end-date-${animal.AnimalId}-${i}`);
                    medicationNumberFieldValidator.removeState(`medication-length-${animal.AnimalId}-${i}`);
                    medicationNumberFieldValidator.removeState(`medication-waiting-time-${animal.AnimalId}-${i}`);
                });
            }
        }
    }

    useEffect(() => {
        if (props.forceReadOnly && props.forceReadOnly === true) setReadOnly(true);

        if (
            auth.canUpdateChainInformationAdmin ||
            (auth.canCreateChainInformation && props.announcementNumber === '')
        )
            setReadOnly(true);
    }, [props.forceReadOnly]);

    useEffect(() => {
        let tempSymptoms: IAnimalSymptoms[] = [];
        let animalsForArray: string[] = [];
        for (let ind = 0; ind < loadedSymptoms.length; ind++) {
            animalsForArray = [];
            let tempObject = loadedSymptoms.find((s) => s.Code === loadedSymptoms[ind].Code);
            let anis = tempObject?.AnimalIds.map((a) => animals.find((ani) => ani.AnimalId === a));
            if (anis && anis.length > 0) {
                for (let animal of anis) {
                    const animalFound = animalsInput.find(
                        (g) => g.text === animal?.EuIdentifier,
                    )?.value;
                    if (animalFound) {
                        animalsForArray.push(animalFound.toString());
                    }
                }
            }
            let include = tempSymptoms.find((t) => t.code.includes(loadedSymptoms[ind].Code));
            if (!include) {
                tempSymptoms.push({ animals: animalsForArray, code: loadedSymptoms[ind].Code });
            }
        }
        setAnimalSymptoms(tempSymptoms);
    }, [loadedSymptoms]);

    useEffect(() => {
        if (props.animals) {
            let anArr: IAnimalAnnouncementAnimal[] = [];

            for (let i = 0; i < props.animals.length; i++) {
                let an: IAnimalAnnouncementAnimal = {
                    AnimalId: props.animals[i].AnimalId,
                    BirthDate: props.animals[i].BirthDate
                        ? props.animals[i].BirthDate.toString()
                        : '',
                    Breed: props.animals[i].Breed,
                    EuIdentifier: props.animals[i].EuIdentifier,
                    SalesType: props.animals[i].SalesType,
                    Sex: props.animals[i].Sex,
                };
                anArr.push(an);
            }
            setAnimals(anArr);
        } else if (props.announcementData) {
            setAnimals(props.announcementData.Batches.map((_) => _.Animals[0]));
        } else {
            setAnimals([]);
        }
    }, [props.animals, props.announcementData]);

    useEffect(() => {
        if (animals.length > 0) {
            let tempAnimals = animals.map((a, i) => {
                return {
                    text: a.EuIdentifier,
                    value: i + 1,
                } as IAnimalInputValue;
            });
            setAnimalsInput(tempAnimals);
        }
    }, [animals]);

    useEffect(() => {
        if (props.announcementData && props.announcementData.Number != 0) {
            setAnnouncementNumber(props.announcementData.Number.toString());
        } else {
            setAnnouncementNumber(props.announcementNumber);
        }
    }, [props]);

    // Sync medicatedAnimals with medicationInfoList when data loads
    useEffect(() => {
        if (animals.length > 0 && medicationInfoList.length > 0) {
            setMedicatedAnimals((previousState) => {
                const updatedMedicatedAnimals = animals.map((animal) => {
                    const hasMedicationInfo = medicationInfoList.some(
                        (info) => info.animalId === animal.AnimalId,
                    );
                    const wasMedicated =
                        previousState.find((m) => m.animalId === animal.AnimalId)?.isMedicated ??
                        false;
                    return {
                        animalId: animal.AnimalId,
                        isMedicated: hasMedicationInfo || wasMedicated,
                    };
                });
                return updatedMedicatedAnimals;
            });
        }
    }, [medicationInfoList, animals]);

    useEffect(() => {
        setIsLoading(true);
        chainInformationApi
            .loadChainInformation(announcementId)
            .then((response) => {
                if (
                    response?.Entity?.ChainInformationId ===
                        '00000000-0000-0000-0000-000000000000' &&
                    !auth.canCreateChainInformation
                ) {
                    setReadOnly(false);
                }
                if (response !== null) {
                    if (
                        response.Entity.ChainInformationId !==
                        '00000000-0000-0000-0000-000000000000'
                    ) {
                        setAdditionalInfo(response.Entity.AdditionalInfo);
                        setAnomalies(response.Entity.Anomalies);
                        setAuthorityRegulations(response.Entity.AuthorityRegulations);
                        setHealthInformation(response.Entity.HealthInformation);
                        setForeignObject(response.Entity.ForeignObject);
                        setForeignObjectInfo(response.Entity.ForeignObjectInfo);
                        setFormConfirmation(response.Entity.FormConfirmation);
                        setGmFree(response.Entity.GmFree);
                        setMedicalAccounting(response.Entity.MedicalAccounting);
                        setMedicatedFeed(response.Entity.MedicatedFeed);
                        setMedicationInfo(response.Entity.MedicationInfo);
                        setNasevaInfo(response.Entity.NasevaInfo);
                        setRegulations(response.Entity.Regulations);
                        setSlaughterHouseInfo(response.Entity.SlaughterHouseInfo);
                        setVetName(response.Entity.VetName);
                        setWaitingTimeOver(response.Entity.WaitingTimeOver);

                        // Get the list of animals from props
                        const animalsList =
                            props.animals ||
                            props.announcementData?.Batches?.flatMap((b) => b.Animals) ||
                            [];

                        setAnimalDiseaseCodes(response.Entity.Diseases.map((d) => d.Code));
                        setAnimalDiseases(response.Entity.Diseases);
                        setAnimalSymptomCodes(response.Entity.Symptoms.map((s) => s.Code));
                        setOtherSymptom(
                            response.Entity.Symptoms.find((i) => i.Info.length > 0)?.Info || '',
                        );
                        setOtherDisease(
                            response.Entity.Diseases.find((i) => i.Info.length > 0)?.Info || '',
                        );
                        setChainInformationId(response.Entity.ChainInformationId);

                        // Load medication details with backward compatibility
                        if (
                            response.Entity.MedicationDetails &&
                            response.Entity.MedicationDetails.length > 0
                        ) {
                            // New format: use the detailed medication info
                            // Normalize the data to ensure property names match (backend might use AnimalId with capital letters)
                            const normalizedMedicationDetails =
                                response.Entity.MedicationDetails.map((detail: any) => ({
                                    animalId: detail.animalId || detail.AnimalId,
                                    euIdentifier: detail.euIdentifier || detail.EuIdentifier || '',
                                    medications: (
                                        detail.medications ||
                                        detail.Medications ||
                                        []
                                    ).map((med: any) => ({
                                        medicationName:
                                            med.medicationName || med.MedicationName || '',
                                        medicationLength:
                                            med.medicationLength || med.MedicationLength || '',
                                        medicationEndDate:
                                            med.medicationEndDate || med.MedicationEndDate || '',
                                        medicationWaitingTime:
                                            med.medicationWaitingTime ||
                                            med.MedicationWaitingTime ||
                                            '',
                                    })),
                                }));
                            setMedicationInfoList(normalizedMedicationDetails);

                            const medicationsSource =
                                response.Entity.Medications ||
                                (response.Entity as any).MedicatedAnimals ||
                                [];
                            const normalizedLegacyMedications = medicationsSource.map((m: any) => {
                                if (typeof m === 'string') {
                                    return { animalId: m, isMedicated: true };
                                }
                                return {
                                    animalId: m.animalId || m.AnimalId,
                                    isMedicated: m.isMedicated ?? m.IsMedicated ?? false,
                                };
                            });

                            // Update medicatedAnimals to reflect which animals have medication details
                            const updatedMedicatedAnimals = animalsList.map((animal) => {
                                const hasMedicationDetails = normalizedMedicationDetails.some(
                                    (detail: IMedicationInfo) =>
                                        detail.animalId === animal.AnimalId,
                                );
                                const hasLegacyMedication =
                                    normalizedLegacyMedications.find(
                                        (m) => m.animalId === animal.AnimalId,
                                    )?.isMedicated ?? false;
                                return {
                                    animalId: animal.AnimalId,
                                    isMedicated: hasMedicationDetails || hasLegacyMedication,
                                };
                            });
                            setMedicatedAnimals(updatedMedicatedAnimals);
                        } else {
                            // Old format: use existing Medications data
                            // Normalize the Medications data to handle both string IDs and object formats
                            const medicationsSource =
                                response.Entity.Medications ||
                                (response.Entity as any).MedicatedAnimals ||
                                [];
                            const normalizedMedications = medicationsSource.map((m: any) => {
                                if (typeof m === 'string') {
                                    return { animalId: m, isMedicated: true };
                                }
                                return {
                                    animalId: m.animalId || m.AnimalId,
                                    isMedicated: m.isMedicated ?? m.IsMedicated ?? false,
                                };
                            });
                            setMedicatedAnimals(normalizedMedications);

                            // Create empty medication details for medicated animals
                            const oldFormatMedications = normalizedMedications
                                .filter((m) => m.isMedicated)
                                .map((m) => ({
                                    animalId: m.animalId,
                                    euIdentifier:
                                        animalsList.find((a) => a.AnimalId === m.animalId)
                                            ?.EuIdentifier || '',
                                    medications: [], // Empty array for old data - fields will be hidden
                                }));
                            setMedicationInfoList(oldFormatMedications);
                        }

                        setAnimalDiseaseCodes(response.Entity.Diseases.map((d) => d.Code));
                        setAnimalDiseases(response.Entity.Diseases);
                        setAnimalSymptomCodes(response.Entity.Symptoms.map((s) => s.Code));
                        setOtherSymptom(
                            response.Entity.Symptoms.find((i) => i.Info.length > 0)?.Info || '',
                        );
                        setOtherDisease(
                            response.Entity.Diseases.find((i) => i.Info.length > 0)?.Info || '',
                        );
                        setChainInformationId(response.Entity.ChainInformationId);

                        setSignature({
                            signatory: response.Entity.Signatory || signature.signatory,
                            signatureDate: response.Entity.SignatureDate
                                ? moment(response.Entity.SignatureDate)
                                : signature.signatureDate,
                        });

                        setAnimalSymptoms([]);
                        setLoadedSymptoms(response.Entity.Symptoms);
                        setConfirmation(response.Entity.Signature);
                        setSamplingOrder(response.Entity.SamplingOrder);
                        setMode('edit');
                        if (!auth.canUpdateChainInformation) {
                            setReadOnly(false);
                        }
                    }
                }
            })
            .catch((error) => {
                // Handle 404 or other errors gracefully - continue with empty data
                if (error.response?.status === 404) {
                    // 404 is expected when no chain information exists yet
                    console.log('Chain information not found, starting with empty form');
                } else {
                    // Log other errors
                    console.error('Error loading chain information:', error);
                }
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [announcementId]);

    const getDiseaseInfo = (code: string) => {
        if (code === '0') {
            return otherDisease;
        } else {
            return '';
        }
    };

    const getSymptomInfo = (code: string) => {
        if (code === '0') {
            return otherSymptom;
        } else {
            return '';
        }
    };

    const radioButtonValidation = () => {
        if (medicatedFeed === true && waitingTimeOver === false) {
            return false;
        }

        if (
            medicatedFeed != null &&
            healthInformation != null &&
            medicalAccounting != null &&
            authorityRegulations != null &&
            foreignObject != null &&
            gmFree != null &&
            confirmation != false
        ) {
            return true;
        } else {
            return false;
        }
    };

    const validateMedicationDetails = (): boolean => {
        // If medical accounting is true, medication details are not required
        if (medicalAccounting === true || medicatedFeed !== true) {
            return true;
        }

        // Check if any medicated animal is missing required medication details
        const medicatedAnimalsList = medicatedAnimals.filter((m) => m.isMedicated);

        for (const medAnimal of medicatedAnimalsList) {
            const medInfo = medicationInfoList.find((m) => m.animalId === medAnimal.animalId);

            // If no medication info exists or medications array is empty, it's invalid
            if (!medInfo || medInfo.medications.length === 0) {
                return false;
            }

            // Check each medication entry has all required fields
            for (const med of medInfo.medications) {
                if (
                    !med.medicationName ||
                    !med.medicationLength ||
                    !med.medicationEndDate ||
                    !med.medicationWaitingTime
                ) {
                    return false;
                }
            }
        }

        return true;
    };

    const validateHealthInformation = (forceCheck?: boolean): boolean => {
        if (validationState.healthInformation && !forceCheck) return true;
        if (
            healthInformation && // Check if ANY health information has been given.
            ((animalSymptomCodes.length <= 0 &&
                animalDiseaseCodes.length <= 0 &&
                !slaughterHouseInfo &&
                !nasevaInfo &&
                (!anomalies || anomalies.length <= 0) &&
                (!samplingOrder || samplingOrder.length <= 0)) ||
                // Check that animals are set for animal-specific health information.
                (animalSymptomCodes.length > 0 && animalSymptoms.length <= 0) ||
                (animalDiseaseCodes.length > 0 && animalDiseaseCodes.length <= 0))
        ) {
            setValidationState((ps: any) => ({ ...ps, healthInformation: false }));
            return false;
        } else setValidationState((ps: any) => ({ ...ps, healthInformation: true }));
        return true;
    };
    useEffect(() => {
        validateHealthInformation();
    }, [
        healthInformation,
        animalSymptoms,
        animalSymptomCodes,
        otherSymptom,
        animalDiseases,
        animalDiseaseCodes,
        otherDisease,
        slaughterHouseInfo,
        nasevaInfo,
        anomalies,
        samplingOrder,
    ]);

    const validateGMFree = (forceCheck?: boolean): boolean => {
        if (validationState.gmFree && !forceCheck) return true;

        if (gmFree && (!additionalInfo || additionalInfo.length <= 0)) {
            setValidationState((ps: any) => ({ ...ps, gmFree: false }));
            return false;
        } else setValidationState((ps: any) => ({ ...ps, gmFree: true }));

        return true;
    };
    useEffect(() => {
        validateGMFree();
    }, [gmFree, additionalInfo]);

    const validateNumberFields = () => {
        function isNumeric(val: string) {
            // I love js
            return /^-?\d*$/.test(val);
        }

        for (let medInfo of medicationInfoList) {
            for (let medication of medInfo.medications) {
                if (!isNumeric(medication.medicationLength)
                    || !isNumeric(medication.medicationWaitingTime)) {
                    return false;
                }
            }
        }

        return !medicationNumberFieldValidator.invalid;
    }

    const dataIsValid = () => {
        let valid = true;

        if (validator.invalid || radioButtonValidation() === false) {
            valid = false;
        }

        valid = validateHealthInformation(true) ? valid : false;
        valid = validateGMFree(true) ? valid : false;
        valid = validateMedicationDetails() ? valid : false;

        return valid;
    };

    const prepareSave = () => {
        let numberFieldsAreValid = true;

        const medicatedAnimalsToSave = (() => {
            const ids = new Set<string>();
            medicatedAnimals.filter((m) => m.isMedicated).forEach((m) => ids.add(m.animalId));
            medicationInfoList.forEach((m) => ids.add(m.animalId));
            return Array.from(ids).map((animalId) => ({
                animalId,
                isMedicated: true,
            }));
        })();

        // i have no idea why && !auth.canUpdateChainInformationAdmin
        // and why changes by admins are ignored
        // seems like admins can only accept the form
        if (!validateNumberFields() && !auth.canUpdateChainInformationAdmin) {
            enqueueSnackbar(t('AnelmaGeneral:1182'), {
                variant: 'error',
            });
            numberFieldsAreValid = false;
            // do not return, check other fields
        }

        if (
            (medicatedAnimalsToSave.length === 0 && medicatedFeed === true) ||
            (medicatedFeed === true && waitingTimeOver === false)
        ) {
            enqueueSnackbar(t('AnelmaChainInformation:1056'), {
                variant: 'error',
            });
            return;
        }

        // i have no idea why && !auth.canUpdateChainInformationAdmin
        // and why changes by admins are ignored
        // seems like admins can only accept the form
        if (!dataIsValid() && !auth.canUpdateChainInformationAdmin) {
            enqueueSnackbar(t('AnelmaGeneral:1169'), {
                variant: 'error',
            });
            return;
        }

        if (!numberFieldsAreValid) {
            return;
        }

        let animalDiseasesToSave: IAnimalDiseases[] = [];

        for (let i = 0; i < animalDiseases.length; i++) {
            let obj: IAnimalDiseases = {
                Code: animalDiseases[i].Code,
                DiscoveryDate: moment(animalDiseases[i].DiscoveryDate).format(dayFormat).toString(),
                Info: getDiseaseInfo(animalDiseases[i].Code),
            };
            animalDiseasesToSave.push(obj);
        }

        let animalSymptomsToSave: IAnimalSymptomsToSave[] = [];

        for (let ind = 0; ind < animalSymptoms.length; ind++) {
            let newSymptom: IAnimalSymptomsToSave = {
                Code: animalSymptoms[ind].code,
                AnimalIds: animalDataMatcher(
                    animalsInput.map((an) => {
                        let str = '';
                        for (let index = 0; index < animalSymptoms[ind].animals.length; index++) {
                            if (
                                animalSymptoms[ind].animals[index].toString() ===
                                an.value.toString()
                            ) {
                                str = an.text;
                            }
                        }
                        return str;
                    }),
                ),
                Info: getSymptomInfo(animalSymptoms[ind].code),
            };

            animalSymptomsToSave.push(newSymptom);
        }

        let confirmationToSave = false;
        if (auth.canUpdateChainInformationAdmin) {
            confirmationToSave = formConfirmation;

            if (chainInformationId) {
                chainInformationApi
                    .saveFormConfirmation(confirmationToSave, chainInformationId)
                    .then((response) => {
                        if (!response) {
                            enqueueSnackbar(t('AnelmaGeneral:1019'), {
                                variant: 'error',
                            });
                            return;
                        }

                        enqueueSnackbar(t('AnelmaGeneral:1018'), {
                            variant: 'success',
                        });

                        props.onClose();
                    });
            } else {
                enqueueSnackbar(t('AnelmaChainInformation:1055'), {
                    variant: 'error',
                });
                props.onClose();
            }
        } else {
            if (mode === 'edit') {
                const newSignature = {
                    signatory: `${user.data.firstName}  ${user.data.lastName}`,
                    signatureDate: moment(),
                };

                let formData: IChainInformationUpdateData = {
                    ChainInformationId: chainInformationId,
                    AnnouncementId: announcementId,
                    MedicatedFeed: medicatedFeed,
                    HealthInformation: healthInformation,
                    AuthorityRegulations: authorityRegulations,
                    ForeignObject: foreignObject,
                    GmFree: gmFree,
                    MedicalAccounting: medicalAccounting,
                    WaitingTimeOver: waitingTimeOver,
                    NasevaInfo: nasevaInfo,
                    SlaughterHouseInfo: slaughterHouseInfo,
                    MedicationInfo: medicationInfo,
                    Anomalies: anomalies,
                    Regulations: regulations,
                    SamplingOrder: samplingOrder,
                    VetName: vetName,
                    AdditionalInfo: additionalInfo,
                    ForeignObjectInfo: foreignObjectInfo,
                    AnimalSymptoms: animalSymptomsToSave,
                    OtherSymptom: otherSymptom,
                    AnimalDiseases: animalDiseasesToSave,
                    OtherDisease: otherDisease,
                    MedicatedAnimals: medicatedAnimalsToSave,
                    Medications: medicatedAnimalsToSave,
                    MedicationDetails: medicationInfoList,
                    FormConfirmation: confirmationToSave,
                    Signature: confirmation,
                    Signatory: newSignature.signatory,
                    SignatureDate: newSignature.signatureDate.format('YYYY-MM-DDTHH:mm:ss'),
                };

                chainInformationApi
                    .updateChainInformation(formData, chainInformationId)
                    .then((response) => {
                        if (!response) {
                            enqueueSnackbar(t('AnelmaGeneral:1019'), {
                                variant: 'error',
                            });
                            return;
                        }

                        enqueueSnackbar(t('AnelmaGeneral:1018'), {
                            variant: 'success',
                        });

                        props.onClose();
                    });
            } else {
                let formData: IChainInformationData = {
                    AnnouncementId: announcementId,
                    MedicatedFeed: medicatedFeed,
                    HealthInformation: healthInformation,
                    AuthorityRegulations: authorityRegulations,
                    ForeignObject: foreignObject,
                    GmFree: gmFree,
                    MedicalAccounting: medicalAccounting,
                    WaitingTimeOver: waitingTimeOver,
                    NasevaInfo: nasevaInfo,
                    SlaughterHouseInfo: slaughterHouseInfo,
                    MedicationInfo: medicationInfo,
                    Anomalies: anomalies,
                    Regulations: regulations,
                    SamplingOrder: samplingOrder,
                    VetName: vetName,
                    AdditionalInfo: additionalInfo,
                    ForeignObjectInfo: foreignObjectInfo,
                    AnimalSymptoms: animalSymptomsToSave,
                    OtherSymptom: otherSymptom,
                    AnimalDiseases: animalDiseasesToSave,
                    OtherDisease: otherDisease,
                    MedicatedAnimals: medicatedAnimalsToSave,
                    Medications: medicatedAnimalsToSave,
                    MedicationDetails: medicationInfoList,
                    FormConfirmation: formConfirmation,
                    Signature: confirmation,
                    Signatory: signature.signatory,
                    SignatureDate: signature.signatureDate.format('YYYY-MM-DDTHH:mm:ss'),
                };
                chainInformationApi.createChainInformation(formData)
                    .then((response) => {
                        if (!response) {
                            enqueueSnackbar(t('AnelmaGeneral:1019'), {
                                variant: 'error',
                            });
                            return;
                        }

                        enqueueSnackbar(t('AnelmaGeneral:1018'), {
                            variant: 'success',
                        });
                        
                        props.onClose(false, response?.Entity?.RelatedAnnouncementId);
                    });
            }
        }
    };

    const animalDataMatcher = (euIdentifiers: string[]) => {
        let ret: string[] = [];
        for (let i = 0; i < euIdentifiers.length; i++) {
            let animalId = animals.find((a) => a.EuIdentifier === euIdentifiers[i])?.AnimalId;
            if (animalId !== undefined && euIdentifiers[i].length > 0) {
                ret.push(animalId);
            }
        }
        return ret;
    };

    const getAnimalsBySymptom = (code: string): number[] => {
        let ret: number[] = [];
        if (animalSymptoms.length > 0) {
            ret =
                animalSymptoms
                    .find((_, i) => Number(_.code) === Number(code))
                    ?.animals.map((a) => Number(a)) || [];
        }
        return ret;
    };

    //Handlers

    const handleAnimalSymptoms = (code: string, value: string[], add: boolean, remove: boolean) => {
        // handles add, update, remove operations
        // TODO: make it better
        if (remove)
            setAnimalSymptoms((prev) => [...prev.filter(s => s.code !== code)]);

        if (add) {
            let newSymptom: IAnimalSymptoms = {
                code: code,
                animals: value,
            };
            setAnimalSymptoms((prev) => [...prev, newSymptom]);
        }
    };

    const handleAnimalSymptomCodes = (code: string) => {
        if (animalSymptomCodes.includes(code)) {
            setAnimalSymptomCodes((previousState) => [
                ...previousState.filter((_, i) => _ !== code),
            ]);
        } else {
            setAnimalSymptomCodes((previousState) => [...previousState, code]);
        }
    };

    const handleAnimalDiseases = (code: string, value: string, add: boolean, remove: boolean) => {
        // handles add, update, remove operations
        // TODO: make it better
        if (remove)
            setAnimalDiseases((prev) => [...prev.filter(d => d.Code !== code)]);

        if (add) {
            let newDisease: IAnimalDiseases = {
                Code: code,
                DiscoveryDate: value,
                Info: '',
            };
            setAnimalDiseases((prev) => [...prev, newDisease]);
        }
    };

    const handleAnimalDiseaseCodes = (code: string) => {
        if (animalDiseaseCodes.includes(code)) {
            setAnimalDiseaseCodes((previousState) => [
                ...previousState.filter((_, i) => _ !== code),
            ]);
        } else {
            setAnimalDiseaseCodes((previousState) => [...previousState, code]);
        }
    };

    const handleToggleAnimalMedication = (animalId: string, euIdentifier: string) => {
        // Check if animal is currently medicated
        const currentlyMedicated =
            medicatedAnimals.find((entry) => entry.animalId === animalId)?.isMedicated ?? false;
        const willBeMedicated = !currentlyMedicated;

        setMedicatedAnimals((previousState) => {
            const index = previousState.findIndex((entry) => entry.animalId === animalId);

            if (index !== -1) {
                const updated = [...previousState];
                updated[index] = {
                    ...updated[index],
                    isMedicated: !updated[index].isMedicated,
                };
                return updated;
            } else {
                return [...previousState, { animalId: animalId, isMedicated: true }];
            }
        });

        // Handle medication info based on checked state
        setMedicationInfoList((previousState) => {
            if (willBeMedicated) {
                // Animal is being checked - initialize medication info if doesn't exist
                const exists = previousState.find((info) => info.animalId === animalId);
                if (!exists) {
                    return [
                        ...previousState,
                        {
                            animalId,
                            euIdentifier,
                            medications: [
                                {
                                    medicationName: '',
                                    medicationLength: '',
                                    medicationEndDate: '',
                                    medicationWaitingTime: '',
                                },
                            ],
                        },
                    ];
                }
                return previousState;
            } else {
                // Animal is being unchecked - remove medication info

                let mi = medicationInfoList.findIndex((info) => info.animalId === animalId);
                if (mi != -1) {
                    medicationInfoList[mi].medications.forEach((m, i) => {
                        validator.removeState(`medication-name-${animalId}-${i}`);
                        validator.removeState(`medication-end-date-${animalId}-${i}`);
                        medicationNumberFieldValidator.removeState(`medication-length-${animalId}-${i}`);
                        medicationNumberFieldValidator.removeState(`medication-waiting-time-${animalId}-${i}`);
                    });
                }

                return previousState.filter((info) => info.animalId !== animalId);
            }
        });
    };

    const handleMedicationInfoChange = (
        animalId: string,
        medicationIndex: number,
        field: keyof IMedicationEntry,
        value: string,
    ) => {
        setMedicationInfoList((previousState) => {
            const index = previousState.findIndex((info) => info.animalId === animalId);
            if (index !== -1) {
                const updated = [...previousState];
                const updatedMedications = [...updated[index].medications];
                updatedMedications[medicationIndex] = {
                    ...updatedMedications[medicationIndex],
                    [field]: value,
                };
                updated[index] = { ...updated[index], medications: updatedMedications };

                return updated;
            }
            return previousState;
        });
    };

    const handleAddMedication = (animalId: string, euIdentifier: string) => {
        setMedicationInfoList((previousState) => {
            const index = previousState.findIndex((info) => info.animalId === animalId);

            // use previous state, if there were some medications
            if (index !== -1 && previousState[index].medications.length < 4) {
                const updated = [...previousState];
                updated[index] = {
                    ...updated[index],
                    medications: [
                        ...updated[index].medications,
                        {
                            medicationName: '',
                            medicationLength: '',
                            medicationEndDate: '',
                            medicationWaitingTime: '',
                        },
                    ],
                };
                return updated;
            }

            // add animal too, if were no medications
            const updated = [...previousState];
            updated.push({
                animalId: animalId,
                euIdentifier: euIdentifier,
                medications: [
                    {
                        medicationName: '',
                        medicationLength: '',
                        medicationEndDate: '',
                        medicationWaitingTime: '',
                    },
                ],
            });

            return updated;
        });
    };

    const handleRemoveMedication = (animalId: string, medicationIndex: number) => {
        setMedicationInfoList((previousState) => {
            const index = previousState.findIndex((info) => info.animalId === animalId);
            if (index !== -1) {
                const updated = [...previousState];
                const updatedMedications = updated[index].medications.filter(
                    (_, idx) => idx !== medicationIndex,
                );
                updated[index] = { ...updated[index], medications: updatedMedications };

                // TODO: refactor
                validator.removeState(`medication-name-${animalId}-${updatedMedications.length}`);
                validator.removeState(`medication-end-date-${animalId}-${updatedMedications.length}`);
                medicationNumberFieldValidator.removeState(`medication-length-${animalId}-${updatedMedications.length}`);
                medicationNumberFieldValidator.removeState(`medication-waiting-time-${animalId}-${updatedMedications.length}`);

                return updated;
            }
            return previousState;
        });
    };

    const handleAnimalSalesTypes = (animals: IAnimalAnnouncementAnimal[]) => {
        let ret = '';
        var salesArray: string[] = [];
        for (let i = 0; i < animals.length; i++) {
            var sales =
                salesTypeParams?.find((s) => s.code.toString() === animals[i].SalesType.toString())
                    ?.text || '';

            salesArray.push(sales);
        }

        for (let index = 0; index < salesArray.length; index++) {
            var item = salesArray.toString().match(salesArray[index]);

            let types = salesArray.filter((s) => s === salesArray[index]);

            if (item !== null)
                if (item.length > 0) {
                    if (!ret.includes(' ' + types[0] + ' ')) {
                        ret = ret + ' ' + types[0].toString() + ' ' + types.length.toString();
                    }
                }
        }

        return ret;
    };

    const getDiseaseDateByCode = (code: string) => {
        let i = animalDiseases.findIndex((d) => d.Code === code);

        if (i !== -1) {
            let d = moment(animalDiseases[i].DiscoveryDate);

            if (d.isValid())
                return d.toDate();
        }

        return null;
    }

    const saveButtonDisabled = () => {
        if (auth.canUpdateChainInformationAdmin) {
            return false;
        } else if (readOnly) {
            return true;
        }
    };

    // UI Elements

    const footerActionButtons: ADialogButtonsProp = {
        left: [
            {
                onClick: () => props.onClose(),
                type: 'cancel',
                disabled: props.newAnnouncement === true,
            },
        ],
        right: [
            {
                onClick: () => prepareSave(),
                type: 'save',
                disabled: saveButtonDisabled(),
            },
        ],
    };

    return (
        <>
            {isLoading === false && (
                <ADialog open onClose={props.onClose}>
                    <ADialogTitle>{t('AnelmaChainInformation:1000')}</ADialogTitle>

                    <ADialogContent size='xl'>
                        <Grid container>
                            <Grid size={{ xs: 12, sm: 4, md: 4, lg: 4 }}>
                                <ATextInput
                                    id={'AnnouncementNumber'}
                                    label={t('AnelmaChainInformation:1001')}
                                    onChange={(v) => null}
                                    validator={validator}
                                    disabled
                                    value={announcementNumber}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 4, md: 4, lg: 4 }}>
                                <ATextInput
                                    id={'AnnouncementAnimals'}
                                    label={t('AnelmaChainInformation:1002')}
                                    onChange={(v) => null}
                                    validator={validator}
                                    disabled
                                    value={handleAnimalSalesTypes(animals)}
                                />
                            </Grid>
                        </Grid>
                        <Grid container>
                            <Grid size={{ xs: 12, sm: 4, md: 4, lg: 4 }}>
                                <ATextInput
                                    id={'slaughter-house'}
                                    label={t('AnelmaChainInformation:1003')}
                                    onChange={(v) => null}
                                    validator={validator}
                                    disabled
                                    value={t('AnelmaChainInformation:1004')}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 4, md: 4, lg: 4 }}>
                                <ATextInput
                                    id={'announcement-animals'}
                                    label={t('AnelmaChainInformation:1005')}
                                    onChange={(v) => null}
                                    validator={validator}
                                    disabled
                                    value={t('AnelmaChainInformation:1006')}
                                />
                            </Grid>
                        </Grid>
                        <Grid container>
                            <Grid size={{ xs: 12, sm: 4, md: 4, lg: 4 }}>
                                <ATextInput
                                    id={'producer-number'}
                                    label={t('AnelmaChainInformation:1007')}
                                    onChange={(v) => null}
                                    validator={validator}
                                    disabled
                                    value={props.producerNumber || parsedContext?.producerId || ''}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 4, md: 4, lg: 4 }}>
                                <ATextInput
                                    id={'company-name'}
                                    label={t('AnelmaChainInformation:1008')}
                                    onChange={(v) => null}
                                    validator={validator}
                                    disabled
                                    value={props.companyName || parsedContext?.customerName || ''}
                                />
                            </Grid>
                        </Grid>
                        <Grid container style={{ paddingLeft: '24px', paddingRight: '24px' }}>
                            <ChainInfoMedication
                                animals={animals}
                                readOnly={readOnly}
                                medicatedFeed={medicatedFeed}
                                onMedicatedFeedChange={(v) => {
                                    setMedicatedFeed(v);
                                    if (!v) {
                                        filterValidator();
                                        setMedicalAccounting(false);
                                        setMedicationInfo('');
                                        setWaitingTimeOver(false);
                                        setMedicatedAnimals([]);
                                        setMedicationInfoList([]);
                                    }
                                }}
                                medicalAccounting={medicalAccounting}
                                onMedicalAccountingChange={(v) => setMedicalAccounting(v)}
                                medicationInfo={medicationInfo}
                                onMedicationInfoTextChange={(v) => setMedicationInfo(v)}
                                waitingTimeOver={waitingTimeOver}
                                onWaitingTimeOverChange={(v) => setWaitingTimeOver(v)}
                                medicatedAnimals={medicatedAnimals}
                                onToggleAnimalMedication={handleToggleAnimalMedication}
                                medicationInfoList={medicationInfoList}
                                onMedicationInfoChange={handleMedicationInfoChange}
                                onAddMedication={handleAddMedication}
                                onRemoveMedication={handleRemoveMedication}
                                validator={validator}
                                medicationNumberFieldValidator={medicationNumberFieldValidator}
                            />
                            <Grid container>
                                <Grid>
                                    <FormControl>
                                        <h5>{t('AnelmaChainInformation:1018')}</h5>
                                        <RadioGroup>
                                            <FormControlLabel
                                                disabled={readOnly}
                                                checked={healthInformation === false}
                                                onChange={() => {
                                                    validator.removeState('anomalies');
                                                    validator.removeState('sampling-order');
                                                    symptoms.forEach(s => {
                                                        validator.removeState(`symptom-animals-${s.code}`);
                                                    })
                                                    validator.removeState('other-symptom');
                                                    validator.removeState('other-disease');

                                                    setHealthInformation(false);
                                                    setAnimalDiseaseCodes([]);
                                                    setAnimalDiseases([]);
                                                    setAnimalSymptomCodes([]);
                                                    setAnimalSymptoms([]);
                                                    setSlaughterHouseInfo(false);
                                                    setOtherDisease('');
                                                    setOtherSymptom('');
                                                    setNasevaInfo(false);
                                                    setAnomalies('');
                                                    setSamplingOrder('');
                                                }}
                                                control={<ARadiobutton />}
                                                label={t('AnelmaChainInformation:1013')}
                                                labelPlacement='end'
                                            />
                                            <FormControlLabel
                                                disabled={readOnly}
                                                style={{
                                                    color: validationState.healthInformation
                                                        ? 'inherit'
                                                        : 'red',
                                                }}
                                                checked={healthInformation === true}
                                                onChange={() => setHealthInformation(true)}
                                                control={<ARadiobutton />}
                                                label={t('AnelmaChainInformation:1019')}
                                                labelPlacement='end'
                                            />
                                        </RadioGroup>
                                    </FormControl>
                                </Grid>
                            </Grid>
                            {healthInformation === true && (
                                <Grid container>
                                    <Grid>
                                        <Grid container>
                                            <Grid>
                                                <Typography>
                                                    {t('AnelmaChainInformation:1020')}
                                                </Typography>
                                            </Grid>
                                            {symptoms.length > 0 &&
                                                symptoms.map((item, index) => (
                                                    <Grid
                                                        container
                                                        style={{ justifyContent: 'space-between' }}
                                                        size={12}
                                                    >
                                                        <Grid>
                                                            {index === 0 && (
                                                                <h5>
                                                                    {t(
                                                                        'AnelmaChainInformation:1021',
                                                                    )}
                                                                </h5>
                                                            )}
                                                            <FormControlLabel
                                                                disabled={readOnly}
                                                                key={index}
                                                                value={animalSymptomCodes.includes(
                                                                    item.code,
                                                                )}
                                                                onChange={(e, checked) => {
                                                                    if (!checked) {
                                                                        validator.removeState(`symptom-animals-${item.code}`);
                                                                        if (item.code === '0')
                                                                            validator.removeState('other-symptom');
                                                                    }

                                                                    handleAnimalSymptomCodes(item.code);
                                                                    handleAnimalSymptoms(item.code, [], checked, !checked);
                                                                }}
                                                                checked={animalSymptomCodes.includes(
                                                                    item.code,
                                                                )}
                                                                control={<ACheckbox />}
                                                                label={item.text}
                                                                labelPlacement='end'
                                                            />
                                                        </Grid>
                                                        {animalSymptomCodes.includes(item.code) && (
                                                            <Grid size={{ sm: 6 }}>
                                                                {index === 0 && (
                                                                    <h5>
                                                                        {t(
                                                                            'AnelmaChainInformation:1010',
                                                                        )}
                                                                    </h5>
                                                                )}
                                                                <AMultiselect
                                                                    disabled={readOnly}
                                                                    getOptionLabel={(
                                                                        o: IAnimalInputValue,
                                                                    ) => o.text}
                                                                    id={`symptom-animals-${item.code}`}
                                                                    items={animalsInput}
                                                                    keyProp='value'
                                                                    label={t(
                                                                        'AnelmaChainInformation:1057',
                                                                    )}
                                                                    onChange={(v) =>
                                                                        handleAnimalSymptoms(
                                                                            item.code,
                                                                            v as string[],
                                                                            true,
                                                                            true
                                                                        )
                                                                    }
                                                                    required
                                                                    validator={validator}
                                                                    value={getAnimalsBySymptom(
                                                                        item.code,
                                                                    )}
                                                                />
                                                            </Grid>
                                                        )}
                                                    </Grid>
                                                ))}
                                            {animalSymptomCodes.includes('0') && (
                                                <Grid size={{ sm: 12 }}>
                                                    <ATextInput
                                                        disabled={readOnly}
                                                        id='other-symptom'
                                                        label={t('AnelmaChainInformation:1046')}
                                                        lengthBoundaryName='AnelmaChainInformation:TextFieldLength'
                                                        onChange={(v) => setOtherSymptom(v)}
                                                        required
                                                        validator={validator}
                                                        value={otherSymptom}
                                                    />
                                                </Grid>
                                            )}
                                        </Grid>

                                        <Grid container>
                                            <Grid>
                                                <h5>{t('AnelmaChainInformation:1022')}</h5>
                                            </Grid>
                                            {diseases.length > 0 &&
                                                diseases.map((item, index) => (
                                                    <Grid
                                                        container
                                                        style={{ justifyContent: 'space-between' }}
                                                        size={12}
                                                    >
                                                        <Grid>
                                                            <FormControlLabel
                                                                disabled={readOnly}
                                                                key={index}
                                                                value={animalDiseaseCodes.includes(
                                                                    item.code,
                                                                )}
                                                                checked={animalDiseaseCodes.includes(
                                                                    item.code,
                                                                )}
                                                                onChange={(e, checked) => {
                                                                    if (!checked) {
                                                                        validator.removeState(`disease-${item.code}`);
                                                                        if (item.code === '0')
                                                                            validator.removeState('other-disease');
                                                                    }

                                                                    handleAnimalDiseaseCodes(
                                                                        item.code,
                                                                    );
                                                                    handleAnimalDiseases(
                                                                        item.code,
                                                                        '',
                                                                        checked,
                                                                        !checked
                                                                    );
                                                                }}
                                                                control={<ACheckbox />}
                                                                label={item.text}
                                                                labelPlacement='end'
                                                            />
                                                        </Grid>
                                                        {animalDiseaseCodes.includes(item.code) && (
                                                            <Grid size={{ sm: 6 }}>
                                                                <ADateInput
                                                                    disabled={readOnly}
                                                                    id={`disease-${item.code}`}
                                                                    label={t(
                                                                        'AnelmaChainInformation:1024',
                                                                    )}
                                                                    onChange={(d) => {
                                                                        handleAnimalDiseases(
                                                                            item.code,
                                                                            (d && moment(d).isValid())
                                                                                ? moment(d).toString()
                                                                                : '',
                                                                            true, 
                                                                            true
                                                                        )
                                                                    }}
                                                                    value={getDiseaseDateByCode(item.code)}
                                                                    validator={validator}
                                                                    // custom validation rule, otherwise "Invalid date" is a valid date
                                                                    error={getDiseaseDateByCode(item.code) === null}
                                                                    // 01.01.1900 is min for the component, Jan is 0
                                                                    minDate={new Date(1900, 0, 1)}
                                                                    required
                                                                ></ADateInput>
                                                            </Grid>
                                                        )}
                                                    </Grid>
                                                ))}
                                            {animalDiseaseCodes.includes('0') && (
                                                <Grid size={{ sm: 12 }}>
                                                    <ATextInput
                                                        disabled={readOnly}
                                                        id='other-disease'
                                                        label={t('AnelmaChainInformation:1047')}
                                                        lengthBoundaryName='AnelmaChainInformation:TextFieldLength'
                                                        onChange={(v) => setOtherDisease(v)}
                                                        required
                                                        validator={validator}
                                                        value={otherDisease}
                                                    />
                                                </Grid>
                                            )}
                                        </Grid>
                                        <Grid container style={{ marginTop: '20px' }}>
                                            <Grid size={{ sm: 12 }}>
                                                <FormControlLabel
                                                    disabled={readOnly}
                                                    value={slaughterHouseInfo}
                                                    onChange={() =>
                                                        setSlaughterHouseInfo(!slaughterHouseInfo)
                                                    }
                                                    control={<ACheckbox />}
                                                    checked={slaughterHouseInfo === true}
                                                    label={t('AnelmaChainInformation:1023')}
                                                    labelPlacement='end'
                                                />
                                            </Grid>

                                            <Grid size={{ sm: 12 }}>
                                                <FormControlLabel
                                                    disabled={readOnly}
                                                    value={nasevaInfo}
                                                    onChange={() => setNasevaInfo(!nasevaInfo)}
                                                    checked={nasevaInfo === true}
                                                    control={<ACheckbox />}
                                                    label={t('AnelmaChainInformation:1053')}
                                                    labelPlacement='end'
                                                />
                                            </Grid>
                                        </Grid>

                                        <Grid container style={{ marginTop: '20px' }}>
                                            <Grid size={{ sm: 12 }}>
                                                <Typography>
                                                    {t('AnelmaChainInformation:1026')}
                                                </Typography>
                                            </Grid>
                                            <Grid size={{ sm: 12 }}>
                                                <ATextInput
                                                    disabled={readOnly}
                                                    id='anomalies'
                                                    label={t('AnelmaChainInformation:1020')}
                                                    lengthBoundaryName='AnelmaChainInformation:TextFieldLength'
                                                    onChange={(v) => setAnomalies(v)}
                                                    validator={validator}
                                                    value={anomalies}
                                                />
                                            </Grid>
                                        </Grid>
                                        <Grid container style={{ marginTop: '20px' }}>
                                            <Grid size={{ sm: 12 }}>
                                                <Typography>
                                                    {t('AnelmaChainInformation:1027')}
                                                </Typography>
                                            </Grid>

                                            <Grid size={{ sm: 12 }}>
                                                <ATextInput
                                                    disabled={readOnly}
                                                    id='sampling-order'
                                                    label={t('AnelmaChainInformation:1028')}
                                                    lengthBoundaryName='AnelmaChainInformation:TextFieldLength'
                                                    onChange={(v) => setSamplingOrder(v)}
                                                    validator={validator}
                                                    value={samplingOrder}
                                                />
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            )}
                            <Grid container>
                                <Grid>
                                    <FormControl>
                                        <h5>{t('AnelmaChainInformation:1029')}</h5>
                                        <RadioGroup>
                                            <FormControlLabel
                                                disabled={readOnly}
                                                checked={authorityRegulations === false}
                                                onChange={() => {
                                                    validator.removeState('regulations');
                                                    setAuthorityRegulations(false);
                                                    setRegulations('');
                                                }}
                                                control={<ARadiobutton />}
                                                label={t('AnelmaChainInformation:1013')}
                                                labelPlacement='end'
                                            />
                                            <FormControlLabel
                                                disabled={readOnly}
                                                checked={authorityRegulations === true}
                                                onChange={() => setAuthorityRegulations(true)}
                                                control={<ARadiobutton />}
                                                label={t('AnelmaChainInformation:1030')}
                                                labelPlacement='end'
                                            />
                                        </RadioGroup>
                                    </FormControl>
                                </Grid>
                                <Grid size={{ sm: 12 }}>
                                    {authorityRegulations === true && (
                                        <ATextInput
                                            disabled={readOnly}
                                            id='regulations'
                                            label={t('AnelmaChainInformation:1031')}
                                            lengthBoundaryName='AnelmaChainInformation:TextFieldLength'
                                            onChange={(v) => setRegulations(v)}
                                            validator={validator}
                                            required={authorityRegulations === true}
                                            value={regulations}
                                        />
                                    )}
                                </Grid>
                            </Grid>

                            <Grid container>
                                <Grid>
                                    <FormControl>
                                        <h5>{t('AnelmaChainInformation:1048')}</h5>
                                        <RadioGroup>
                                            <FormControlLabel
                                                disabled={readOnly}
                                                checked={foreignObject === false}
                                                onChange={() => {
                                                    validator.removeState('foreign-object');
                                                    setForeignObject(false);
                                                    setForeignObjectInfo('');
                                                }}
                                                control={<ARadiobutton />}
                                                label={t('AnelmaChainInformation:1013')}
                                                labelPlacement='end'
                                            />
                                            <FormControlLabel
                                                disabled={readOnly}
                                                checked={foreignObject === true}
                                                onChange={() => {
                                                    setForeignObject(true);
                                                }}
                                                control={<ARadiobutton />}
                                                label={t('AnelmaChainInformation:1032')}
                                                labelPlacement='end'
                                            />
                                        </RadioGroup>
                                    </FormControl>
                                </Grid>
                                <Grid size={{ sm: 12 }}>
                                    {foreignObject === true && (
                                        <ATextInput
                                            disabled={readOnly}
                                            id='foreign-object'
                                            label={t('AnelmaChainInformation:1033')}
                                            onChange={(v) => setForeignObjectInfo(v)}
                                            lengthBoundaryName='AnelmaChainInformation:TextFieldLength'
                                            validator={validator}
                                            required={foreignObject === true}
                                            value={foreignObjectInfo}
                                        />
                                    )}
                                </Grid>
                            </Grid>
                            <Grid container size={12}>
                                <Grid>
                                    <h5>{t('AnelmaChainInformation:1034')}</h5>
                                    <ATextInput
                                        disabled={readOnly}
                                        id='vet-name'
                                        label={t('AnelmaChainInformation:1035')}
                                        lengthBoundaryName='AnelmaChainInformation:TextFieldLength'
                                        onChange={(v) => setVetName(v)}
                                        required
                                        validator={validator}
                                        value={vetName}
                                    />
                                </Grid>
                            </Grid>
                            <Grid container size={12}>
                                <Grid>
                                    <FormControl>
                                        <h5>{t('AnelmaChainInformation:1036')}</h5>
                                        <RadioGroup>
                                            <FormControlLabel
                                                disabled={readOnly}
                                                checked={gmFree === false}
                                                onChange={() => {
                                                    setGmFree(false);
                                                }}
                                                control={<ARadiobutton />}
                                                label={t('AnelmaChainInformation:1037')}
                                                labelPlacement='end'
                                            />
                                            <FormControlLabel
                                                disabled={readOnly}
                                                style={{
                                                    color: validationState.gmFree
                                                        ? 'inherit'
                                                        : 'red',
                                                }}
                                                checked={gmFree === true}
                                                onChange={() => setGmFree(true)}
                                                control={<ARadiobutton />}
                                                label={t('AnelmaChainInformation:1038')}
                                                labelPlacement='end'
                                            />
                                        </RadioGroup>
                                    </FormControl>
                                </Grid>
                            </Grid>
                            <Grid container size={12}>
                                <Grid size={{ sm: 12 }}>
                                    <h5>{t('AnelmaChainInformation:1039')}</h5>
                                </Grid>
                                <Grid size={{ sm: 12 }}>
                                    <ATextarea
                                        disabled={readOnly}
                                        id='additional-info'
                                        label={''}
                                        onChange={(v) => setAdditionalInfo(v)}
                                        validator={validator}
                                        required={gmFree || false}
                                        value={additionalInfo}
                                        lengthBoundaryName='AnelmaChainInformation:AdditionalInfoLength'
                                        multiline
                                    />
                                </Grid>
                            </Grid>
                            <Grid>
                                <h5>{t('AnelmaChainInformation:1040')}</h5>

                                <FormControlLabel
                                    disabled={readOnly}
                                    value={confirmation}
                                    onChange={() => setConfirmation(!confirmation)}
                                    control={<ACheckbox />}
                                    checked={confirmation === true}
                                    label={t('AnelmaChainInformation:1041')}
                                    labelPlacement='end'
                                />
                            </Grid>

                            <Grid container size={12}>
                                <Grid>
                                    <ADateInput
                                        id='save-date'
                                        label={t('AnelmaChainInformation:1043')}
                                        onChange={() => {}}
                                        disabled
                                        value={signature.signatureDate.toDate()}
                                        validator={validator}
                                        withoutContainer
                                    ></ADateInput>
                                </Grid>
                                <Grid>
                                    <ATextInput
                                        id='signature'
                                        label={t('AnelmaChainInformation:1044')}
                                        onChange={() => {}}
                                        disabled
                                        value={signature.signatory}
                                        validator={validator}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid style={{ textAlign: 'center', marginTop: '30px' }}>
                            {auth.canUpdateChainInformationAdmin && (
                                <ASwitch
                                    checked={formConfirmation}
                                    id='form-confirmation'
                                    label={t('AnelmaChainInformation:1045')}
                                    onChange={(v) => setFormConfirmation(v)}
                                    validator={validator}
                                ></ASwitch>
                            )}
                        </Grid>
                    </ADialogContent>
                    <ADialogActions buttons={footerActionButtons}></ADialogActions>
                </ADialog>
            )}
        </>
    );
}
